import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import {
    AdminAreaDto,
    AdminAreaOccupancyDataDto,
    AdminShowInformationDto
} from '../models/admin-api.model';
import {
    RebookAreaOption,
    RebookBlockOption,
    RebookShowOption,
    RebookTableOption
} from '../models/show.model';
import { extractShowBlocks, mapSlotTimeRange, normalizeTime } from '../utils/admin-enum.utils';

@Injectable({
    providedIn: 'root'
})
export class AdminRebookMapperService {
    toRebookShowOptions(shows: AdminShowInformationDto[], musicalName: string): RebookShowOption[] {
        return shows.map(show => ({
            value: `${show.showID}`,
            showId: show.showID,
            label: `${musicalName} – ${show.showDate ? formatDate(show.showDate, 'EEE, dd.MM.', 'de-DE') : '—'} ${normalizeTime(show.showTime)} Uhr`,
            blocks: this.buildBlockOptions(show)
        }));
    }

    private buildBlockOptions(show: AdminShowInformationDto): RebookBlockOption[] {
        const availableBlocks = extractShowBlocks(show);

        return availableBlocks.map(label => {
            const slot = this.labelToSlot(label);
            const occupancyAreas = this.getAreasOccupancyForSlot(show, slot);
            const planAreas = this.getAreasForSlot(show, slot);

            return {
                value: `${show.showID}-${slot}`,
                label: `${label} (${mapSlotTimeRange(slot, show)})`,
                slot,
                areas: this.buildAreaOptions(planAreas, occupancyAreas)
            };
        });
    }

    private buildAreaOptions(
        areas: AdminAreaDto[],
        occupancyAreas: AdminAreaOccupancyDataDto[]
    ): RebookAreaOption[] {
        return areas.map(area => {
            const matchedOccupancyArea = occupancyAreas.find(item => item.areaID === area.areaId);

            const tables: RebookTableOption[] = (area.tables ?? []).map(table => {
                const matchedOccupancyTable = matchedOccupancyArea?.tablesOccupancyData?.find(
                    occupancyTable => occupancyTable.tableNumber === table.tableNumber
                );

                return {
                    tableNumber: table.tableNumber ?? 0,
                    areaId: area.areaId ?? '',
                    areaName: area.areaName ?? 'Bereich',
                    freeSeatedPlaces: matchedOccupancyTable?.freeSeats ?? 0,
                    totalSeatedPlaces: matchedOccupancyTable?.seatCapacity ?? 0,
                    freeStandingPlaces: matchedOccupancyTable?.freeStandingPlaces ?? 0,
                    totalStandingPlaces: matchedOccupancyTable?.standingCapacity ?? 0
                };
            });

            return {
                areaId: area.areaId ?? '',
                areaName: area.areaName ?? 'Bereich',
                tables
            };
        });
    }

    private getAreasForSlot(show: AdminShowInformationDto, slot: number): AdminAreaDto[] {
        const areasBySlot = show.tableRoomPlan?.areasBySlot;

        if (!areasBySlot) {
            return [];
        }

        switch (slot) {
            case 0:
                return areasBySlot.Before ?? [];
            case 1:
                return areasBySlot.Intermission ?? [];
            case 2:
                return areasBySlot.After ?? [];
            default:
                return [];
        }
    }

    private getAreasOccupancyForSlot(show: AdminShowInformationDto, slot: number): AdminAreaOccupancyDataDto[] {
        const perSlot = show.occupancyInformationsPerSlot;

        if (!perSlot) {
            return [];
        }

        switch (slot) {
            case 0:
                return perSlot.Before?.areasOccupancyInformations ?? [];
            case 1:
                return perSlot.Intermission?.areasOccupancyInformations ?? [];
            case 2:
                return perSlot.After?.areasOccupancyInformations ?? [];
            default:
                return [];
        }
    }

    private labelToSlot(label: string): number {
        switch (label) {
            case 'Vor der Show':
                return 0;
            case 'Pause':
                return 1;
            case 'Nach der Show':
                return 2;
            default:
                return 0;
        }
    }
}